import type { ClientLpPositionIdentity, ClientPositionObservation } from "@solomon-apps/shared";

import { orgApiFetch } from "./orgApiFetch";

/* ── Versioned discovery contract ─────────────────────────────────────────
 *
 * The client-facing discovery shape from
 * `claude-docs/position-discovery-flow/plan.md` ("Discovery contract"):
 * versioned from day one, expressed in catalog vocabulary, and designed for
 * the Jachin provider's eventual richness. The server mirrors this shape in
 * `yaas-api/src/api/org/positions/PositionDiscoveryService.ts`; which
 * provider answers (the local resolver now, Jachin later) is a backend
 * config concern the flow never sees.
 */

/** Cryptographic source classification — distinct from the management-axis
 *  `ProgramPositionSourceKind`. `program-controlled` is reserved for the
 *  Jachin provider. */
export type DiscoverySourceClassification = "eoa" | "multisig-squads" | "program-controlled";

/** Resolved Squads detail for a `multisig-squads` classification. */
export interface DiscoveredSquadsDetail {
    multisigAddress: string;
    vaultAddress?: string;
    threshold: number;
    memberCount: number;
}

/** The Program Surface the holding maps to within the assigned program's scan
 *  scope, or the found-but-not-covered marker (hard "contact your issuer"). */
export type DiscoveryMatch = { kind: "matched"; programSurfaceUuid: string } | { kind: "not-covered" };

export interface DiscoveredPosition {
    /** Contract version; bump on shape changes so provider swaps stay safe. */
    version: 1 | 2;
    /** Catalog surface: wallet-account for the enrollment anchor, the LP protocol slug (e.g. raydium-cpmm) for LPs. */
    surfaceTypeSlug: string;
    /** Wallet/vault for Address; personal-position account for an LP. */
    instance: { address: string };
    sourceClassification: DiscoverySourceClassification;
    /** Present iff `sourceClassification` is `multisig-squads`. */
    squads: DiscoveredSquadsDetail | null;
    /** Decimal USDv principal; null is unavailable, zero is observed zero.
     * Display-only — never an input to settlement. */
    observedBalance: string | null;
    /** ISO timestamp of the observation. */
    observedAt: string;
    lpPosition?: ClientLpPositionIdentity;
    observation?: ClientPositionObservation;
    match: DiscoveryMatch;
    /** Provider evidence, independently nullable. */
    detectorVersion: string | null;
    confidence: number | null;
}

export interface DiscoverPositionsInput {
    /** USDv is the only discoverable asset in MVP. */
    asset: "USDv";
    /** The pasted source: wallet, Squads multisig, or Squads vault. */
    address: string;
    verificationMode?: "direct-solomon";
}

/** The provider seam. The endpoint decides which provider answers (the
 *  `positions.discoveryProvider` gate in yaas-api); callers never know. */
export type DiscoverPositions = (input: DiscoverPositionsInput) => Promise<DiscoveredPosition[]>;

/* ── Wire adapter ────────────────────────────────────────────────────────
 * The endpoint omits `squads` for EOA sources and sends the Squads vault as
 * an explicit null when unknown; normalize both back into the contract shape
 * so flow code never branches on wire quirks. */

interface DiscoverWireSquadsDetail {
    multisigAddress: string;
    vaultAddress?: string | null;
    threshold: number;
    memberCount: number;
}

interface DiscoverWirePosition {
    version: 1 | 2 | "1" | "2";
    surfaceTypeSlug: string;
    instance: { address: string };
    sourceClassification: DiscoverySourceClassification;
    squads?: DiscoverWireSquadsDetail | null;
    observedBalance: string | null;
    observedAt: string;
    lpPosition?: ClientLpPositionIdentity;
    observation?: ClientPositionObservation;
    match: { kind: "matched" | "not-covered"; programSurfaceUuid?: string };
    detectorVersion?: string | null;
    confidence?: number | null;
}

interface DiscoverWireResponse {
    positions: DiscoverWirePosition[];
    lpDiscovery?: LpDiscoveryAvailability;
}

function normalizeDiscoveredPosition(wire: DiscoverWirePosition): DiscoveredPosition {
    return {
        version: wire.version === 2 || wire.version === "2" ? 2 : 1,
        surfaceTypeSlug: wire.surfaceTypeSlug,
        instance: wire.instance,
        sourceClassification: wire.sourceClassification,
        squads: wire.squads
            ? {
                  multisigAddress: wire.squads.multisigAddress,
                  ...(wire.squads.vaultAddress ? { vaultAddress: wire.squads.vaultAddress } : {}),
                  threshold: wire.squads.threshold,
                  memberCount: wire.squads.memberCount
              }
            : null,
        observedBalance: wire.observedBalance,
        observedAt: wire.observedAt,
        ...(wire.lpPosition ? { lpPosition: wire.lpPosition } : {}),
        ...(wire.observation ? { observation: wire.observation } : {}),
        match:
            wire.match.kind === "matched" && wire.match.programSurfaceUuid
                ? { kind: "matched", programSurfaceUuid: wire.match.programSurfaceUuid }
                : { kind: "not-covered" },
        detectorVersion: wire.detectorVersion ?? null,
        confidence: wire.confidence ?? null
    };
}

/**
 * `POST /org/client/positions/discover` — scan the pasted wallet for
 * positions matching the assigned program's surfaces (the scan scope).
 * Classification, canonicalization, balance observation, and matching all
 * happen server-side; the response is a transient preview (nothing is
 * persisted until verify + submit complete). Throws on non-OK responses.
 */
export interface LpDiscoveryAvailability {
    status: "available" | "unavailable" | "not-applicable";
    complete: boolean;
    incompleteReason: string | null;
}

export interface ClientDiscoveryResult {
    positions: DiscoveredPosition[];
    lpDiscovery: LpDiscoveryAvailability | null;
}

export async function discoverClientHoldings(input: DiscoverPositionsInput): Promise<ClientDiscoveryResult> {
    const response = await orgApiFetch<DiscoverWireResponse>("/api/org/client/positions/discover", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input)
    });
    return {
        positions: response.positions.map(normalizeDiscoveredPosition),
        lpDiscovery: response.lpDiscovery ?? null
    };
}

export const discoverClientPositions: DiscoverPositions = async (input) =>
    (await discoverClientHoldings(input)).positions;
